import { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase/client';
import { projectId, API_PUBLIC_BASE } from '../utils/supabase/info';
import { CURRENT_TERMS_VERSION } from '../utils/terms-version';
import { Sparkles, ArrowLeft, Check, X, Info } from 'lucide-react';
import { Loader } from './ui/Loader';
import { EmailVerificationModal } from './EmailVerificationModal';
import { toast } from 'sonner';
import { devLog, devError } from '../utils/logger';
import { isSwUpdateGuardActive } from '../utils/swUpdateState';
import { trackFunnelEvent } from '../utils/funnelTracking';


interface AuthPageProps {
  /** Called with session on success. May return a Promise; AuthPage keeps loading until it resolves (profile fetch + navigate). */
  onAuthSuccess: (session: any) => void | Promise<void>;
  onForgotPassword?: () => void;
  showTermsLink?: boolean;
  onTermsClick?: () => void;
  onFaqClick?: () => void;
  onBack?: () => void;
}

export function AuthPage({ onAuthSuccess, onForgotPassword, showTermsLink = true, onTermsClick, onFaqClick, onBack }: AuthPageProps) {
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameError, setUsernameError] = useState<string>(''); // ✅ NEW: Store specific error message
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [userType, setUserType] = useState<'talent' | 'employer'>('talent');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [pendingVerificationUserType, setPendingVerificationUserType] = useState<'talent' | 'employer'>('talent');
  const [isAfterSignup, setIsAfterSignup] = useState(false); // Track if modal shown after signup (no close button)
  
  // Employer-specific fields
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [organizationRole, setOrganizationRole] = useState('');
  const [organizationRoleOther, setOrganizationRoleOther] = useState('');
  const [fullName, setFullName] = useState(''); // Employer's full legal name
  
  const containerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Scroll to top when switching between sign in and sign up
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSignUp]);

  // ✅ NEW: Check for pending verification on mount
  useEffect(() => {
    const pendingEmail = localStorage.getItem('pendingVerificationEmail');
    
    if (pendingEmail) {
      devLog('✅ Found pending verification in localStorage:', pendingEmail);
      devLog('⚠️ Auto-showing email verification modal');
      
      setPendingVerificationEmail(pendingEmail);
      setPendingVerificationUserType('talent'); // Default to talent, will be updated if user logs in
      setShowEmailVerificationModal(true);
      setIsAfterSignup(false); // Allow closing modal since this is a return visit
    }
  }, []); // Run only once on mount

  // Translate common Supabase auth errors to Norwegian
  const translateAuthError = (error: string): string => {
    if (error.includes('Invalid login credentials')) return 'Feil e-post eller passord';
    if (error.includes('Email not confirmed')) return 'E-post må bekreftes';
    if (error.includes('User not found')) return 'Bruker ikke funnet';
    if (error.includes('Invalid email')) return 'Ugyldig e-postadresse';
    if (error.includes('Email already registered')) return 'E-post er allerede registrert';
    if (error.includes('A user with this email address has already been registered')) return 'En bruker med denne e-postadressen er allerede registrert';
    if (error.includes('Password should be')) return 'Passordet er for svakt';
    if (error.includes('New password should be different from the old password')) return 'Nytt passord må være forskjellig fra det gamle passordet';
    if (error.includes('Unable to validate email address')) return 'Kunne ikke validere e-postadresse';
    if (error.includes('Invalid credentials')) return 'Ugyldig påloggingsinformasjon';
    if (error.includes('User already registered')) return 'Brukeren er allerede registrert';
    if (error.includes('Invalid refresh token')) return 'Ugyldig eller utløpt tilbakestillingslenke';
    if (error.includes('Auth session missing')) return 'Ugyldig tilbakestillingslenke';
    if (error.includes('For security purposes')) return 'Av sikkerhetshensyn må du vente litt før du prøver igjen';
    if (error.includes('Email rate limit exceeded')) return 'For mange e-poster sendt. Vennligst vent litt før du prøver igjen';
    if (error.includes('Signups not allowed')) return 'Registrering er midlertidig deaktivert';
    if (error.includes('Password is too weak')) return 'Passordet er for svakt';
    // Handle our custom password requirement errors (these are already in Norwegian from the server)
    if (error.includes('Passord må')) return error;
    if (error.includes('Brukernavn')) return error; // Username errors already in Norwegian
    if (error.includes('brukernavnet')) return error; // Username errors already in Norwegian
    if (error.includes('session') && error.toLowerCase().includes('expired')) return 'Økten har utløpt. Logg inn på nytt.';
    if (error.includes('Failed to fetch') || error.includes('NetworkError') || error.includes('Load failed')) return 'Nettverksfeil. Sjekk internett og prøv igjen.';
    if (error.includes('CORS') || error.includes('cross-origin')) return 'Tilkoblingsfeil. Prøv igjen eller åpne appen i en annen nettleser.';
    return error;
  };

  /** Run onAuthSuccess and keep loading until parent completes (profile fetch + navigate). Clears loading in finally. */
  const completeAuthSuccess = async (session: any) => {
    try {
      devLog('✅ Auth successful, calling onAuthSuccess (loading stays until parent completes)');
      await Promise.resolve(onAuthSuccess(session));
    } catch (err) {
      devError('onAuthSuccess failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);

    try {
      devLog('🔐 Attempting sign in...', { email });
      devLog('🔑 Calling signInWithPassword...');
      const signInStart = Date.now();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      const signInDuration = Date.now() - signInStart;
      
      devLog('📬 Sign in response:', { 
        hasData: !!data, 
        hasError: !!error, 
        error: error?.message,
        duration: `${signInDuration}ms`
      });

      if (error) {
        // ✅ DEBUG: Log actual error message
        devLog('🔴 Sign in error:', error.message);
        
        // ✅ NEW: Show modal instead of error for unconfirmed email
        if (error.message.includes('Email not confirmed')) {
          setPendingVerificationEmail(email);
          setPendingVerificationUserType(userType);
          setShowEmailVerificationModal(true);
          setIsAfterSignup(false); // Can close modal when trying to sign in
          setLoading(false);
          return;
        }
        
        setError(translateAuthError(error.message));
        setLoading(false);
        return;
      }

      // ✅ CRITICAL SECURITY FIX: Verify email is actually confirmed before allowing access
      // This prevents iPhone and other edge cases where signInWithPassword succeeds without email verification
      if (data.session) {
        // Fetch user data to check email_confirmed_at
        let userData: any = null;
        let userError: any = null;
        try {
          const result = await supabase.auth.getUser();
          userData = result.data;
          userError = result.error;
        } catch (getUserErr) {
          // 🔒 If getUser() throws AbortError (e.g. during SW update) but signIn succeeded,
          // skip the email verification check and proceed with the session we already have.
          // The session from signInWithPassword is valid — getUser() just couldn't reach the server.
          const isAbort = getUserErr instanceof Error && (getUserErr.name === 'AbortError' || getUserErr.message?.includes('aborted'));
          if (isAbort) {
            devLog('⚠️ getUser() aborted (likely SW update) — proceeding with signIn session');
            setShowEmailVerificationModal(false);
            localStorage.removeItem('pendingVerificationEmail');
            await completeAuthSuccess(data.session);
            return;
          }
          // Re-throw non-abort errors
          throw getUserErr;
        }
        
        if (userError || !userData?.user) {
          setError('Kunne ikke verifisere brukerstatus. Prøv igjen.');
          setLoading(false);
          // ✅ Wrap signOut in try-catch to prevent generic error message
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            devError('Failed to sign out:', signOutError);
          }
          return;
        }

        // Check if email is actually confirmed
        if (!userData.user.email_confirmed_at) {
          // Email is NOT confirmed - block access and keep modal open
          setError('E-post må bekreftes før du kan logge inn. Sjekk innboksen din og klikk på bekreftelseslenken.');
          setLoading(false);
          // ✅ Wrap signOut in try-catch to prevent generic error message
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            devError('Failed to sign out:', signOutError);
          }
          return;
        }
      }

      // ✅ Email is verified - NOW close modal and proceed with login
      setShowEmailVerificationModal(false);
      
      // ✅ Clear pending verification from localStorage
      localStorage.removeItem('pendingVerificationEmail');
      
      await completeAuthSuccess(data.session);
    } catch (err) {
      devError('❌ Sign in exception caught:', err);
      devError('Exception type:', err instanceof Error ? err.constructor.name : typeof err);
      devError('Exception message:', err instanceof Error ? err.message : String(err));
      devError('Exception stack:', err instanceof Error ? err.stack : 'No stack');
      
      // Better error messages
      const isSwUpdating = isSwUpdateGuardActive();

      if ((err instanceof Error && (err.name === 'AbortError' || err.message.includes('aborted'))) || isSwUpdating) {
        // During SW update: don't scare the user — the app will reload momentarily
        if (isSwUpdating) {
          devLog('🤫 Ignoring auth error during SW update');
          setError('');
          setLoading(false);
          return; // Silently swallow — page will reload
        }
        // AbortError outside of a flagged SW update — likely locks got disrupted.
        // Auto-retry once after a short pause instead of showing a scary message.
        devLog('💡 AbortError — auto-retrying login in 1.5s...');
        setError('');
        await new Promise(resolve => setTimeout(resolve, 1500));
        try {
          const { data: retryData, error: retryError } = await supabase.auth.signInWithPassword({ email, password });
          if (!retryError && retryData.session) {
            devLog('✅ Auto-retry login succeeded');
            await completeAuthSuccess(retryData.session);
            return;
          }
          devLog('⚠️ Auto-retry failed:', retryError?.message);
        } catch (retryErr) {
          devLog('⚠️ Auto-retry threw:', retryErr);
        }
        setError('Appen ble nettopp oppdatert. Vennligst prøv å logge inn igjen.');
      } else if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Nettverksfeil. Sjekk internettforbindelsen din og prøv igjen.');
      } else if (err instanceof Error) {
        devError('🔴 Actual error:', err.message);
        const translated = translateAuthError(err.message);
        setError(translated !== err.message ? translated : `Kunne ikke logge inn: ${err.message}`);
      } else {
        setError('Kunne ikke logge inn. Prøv igjen senere.');
      }
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Username validation
    if (!username || username.trim().length < 3) {
      setError('Brukernavn må være minst 3 tegn');
      setLoading(false);
      return;
    }
    
    if (username.trim().length > 20) {
      setError('Brukernavn kan ikke være lengre enn 20 tegn');
      setLoading(false);
      return;
    }
    
    // Username format validation (matches backend and database)
    const usernameRegex = /^[a-zA-Z0-9_\s-]+$/;
    
    if (!usernameRegex.test(username.trim())) {
      setError('Brukernavn kan bare inneholde bokstaver, tall, mellomrom, bindestrek og understrek');
      setLoading(false);
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Ugyldig e-postadresse');
      setLoading(false);
      return;
    }

    // Enhanced password validation
    if (!password || password.length < 8) {
      setError('Passord må være minst 8 tegn');
      setLoading(false);
      return;
    }
    
    if (!/[A-Z]/.test(password)) {
      setError('Passord må inneholde minst én stor bokstav');
      setLoading(false);
      return;
    }
    
    if (!/[a-z]/.test(password)) {
      setError('Passord må inneholde minst én liten bokstav');
      setLoading(false);
      return;
    }
    
    if (!/[0-9]/.test(password)) {
      setError('Passord må inneholde minst ett tall');
      setLoading(false);
      return;
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      setError('Passord må inneholde minst ett spesialtegn (!@#$%^&* osv.)');
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setError('Du må godta vilkårene for å registrere deg');
      setLoading(false);
      return;
    }

    if (!acceptedPrivacy) {
      setError('Du må godta personvernerklæringen for å registrere deg');
      setLoading(false);
      return;
    }

    try {
      trackFunnelEvent('start_signup');
      const response = await fetch(
        `${API_PUBLIC_BASE}/signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email, 
            password, 
            username,
            userType,
            termsAcceptedAt: new Date().toISOString(), // Send timestamp when terms were accepted
            termsVersion: CURRENT_TERMS_VERSION, // Send version of terms accepted
            organizationNumber: userType === 'employer' ? organizationNumber : null,
            organizationRole: userType === 'employer' ? organizationRole : null,
            organizationRoleOther: userType === 'employer' ? organizationRoleOther : null,
            fullName: userType === 'employer' ? fullName : null,
          }),
        }
      );

      const data = await response.json();

      // ✅ Handle rate limiting (429)
      if (response.status === 429) {
        setError(data.error || 'For mange registreringsforsøk. Prøv igjen om 15 minutter');
        toast.error(data.error || 'For mange registreringsforsøk. Prøv igjen om 15 minutter');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const message = data.error || 'Kunne ikke opprette konto';
        setError(message);
        toast.error(message);
        setLoading(false);
        return;
      }

      // ✅ Show email verification modal immediately after successful signup
      // No auto-login attempt - user must verify email first
      setPendingVerificationEmail(email);
      setPendingVerificationUserType(userType);
      setShowEmailVerificationModal(true);
      setIsAfterSignup(true); // Set flag to indicate modal shown after signup
      
      // ✅ NEW: Store pending verification in localStorage for auto-re-show on return
      localStorage.setItem('pendingVerificationEmail', email);
      
      setLoading(false);
    } catch (err) {
      devError('Sign up error:', err);
      const message = 'Kunne ikke opprette konto. Prøv igjen senere.';
      setError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  const checkUsernameAvailability = async (username: string) => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      setUsernameError(''); // ✅ Clear error
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(
        `${API_PUBLIC_BASE}/check-username/${username.toLowerCase()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setUsernameAvailable(data.available);
        // ✅ NEW: Store specific error message if username is unavailable
        if (!data.available && data.error) {
          setUsernameError(data.error);
        } else {
          setUsernameError(''); // Clear error if available
        }
      } else {
        setUsernameAvailable(null);
        setUsernameError(''); // Clear error on request failure
      }
    } catch (err) {
      devError('Error checking username:', err);
      setUsernameAvailable(null);
      setUsernameError(''); // Clear error on exception
    } finally {
      setCheckingUsername(false);
    }
  };

  // Debounce username check (wait 500ms after user stops typing)
  useEffect(() => {
    if (!isSignUp || !username) {
      setUsernameAvailable(null);
      setUsernameError(''); // ✅ Clear error when switching views or clearing username
      return;
    }

    const timer = setTimeout(() => {
      checkUsernameAvailability(username);
    }, 500);

    return () => clearTimeout(timer);
  }, [username, isSignUp]);

  // Handler for retry login after verification
  const handleRetryLogin = async () => {
    // ✅ FIX: Don't close modal until we know login succeeded
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: pendingVerificationEmail,
        password,
      });

      if (error) {
        // If still not confirmed, keep modal open and show error
        if (error.message.includes('Email not confirmed')) {
          setError('E-post må bekreftes før du kan logge inn. Sjekk innboksen din og klikk på bekreftelseslenken.');
          setLoading(false);
          return;
        }
        
        setError(translateAuthError(error.message));
        setLoading(false);
        return;
      }

      // ✅ CRITICAL SECURITY FIX: Verify email is actually confirmed before allowing access
      // This prevents iPhone and other edge cases where signInWithPassword succeeds without email verification
      if (data.session) {
        // Fetch user data to check email_confirmed_at
        let userData: any = null;
        let userError: any = null;
        try {
          const result = await supabase.auth.getUser();
          userData = result.data;
          userError = result.error;
        } catch (getUserErr) {
          // During SW updates, lock aborts can interrupt getUser() even when sign-in succeeded.
          const isAbort = getUserErr instanceof Error && (getUserErr.name === 'AbortError' || getUserErr.message?.includes('aborted'));
          if (isAbort && isSwUpdateGuardActive()) {
            devLog('⚠️ Retry getUser() aborted during SW update — proceeding with signIn session');
            setShowEmailVerificationModal(false);
            localStorage.removeItem('pendingVerificationEmail');
            await completeAuthSuccess(data.session);
            return;
          }
          throw getUserErr;
        }
        
        if (userError || !userData?.user) {
          setError('Kunne ikke verifisere brukerstatus. Prøv igjen.');
          setLoading(false);
          // ✅ Wrap signOut in try-catch to prevent generic error message
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            devError('Failed to sign out:', signOutError);
          }
          return;
        }

        // Check if email is actually confirmed
        if (!userData.user.email_confirmed_at) {
          // Email is NOT confirmed - block access and keep modal open
          setError('E-post ikke verifisert. Sjekk innboksen din og klikk på bekreftelseslenken.');
          setLoading(false);
          // ✅ Wrap signOut in try-catch to prevent generic error message
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            devError('Failed to sign out:', signOutError);
          }
          return;
        }
      }

      // ✅ Email is verified - NOW close modal and proceed with login
      setShowEmailVerificationModal(false);
      
      // ✅ Clear pending verification from localStorage
      localStorage.removeItem('pendingVerificationEmail');
      
      await completeAuthSuccess(data.session);
    } catch (err) {
      devError('Retry login error:', err);
      const isSwUpdating = isSwUpdateGuardActive();
      if ((err instanceof Error && (err.name === 'AbortError' || err.message.includes('aborted'))) || isSwUpdating) {
        // During update, avoid scary error text - app/session will stabilize shortly.
        setError('');
        setLoading(false);
        return;
      }
      setError(err instanceof Error ? translateAuthError(err.message) : 'Kunne ikke logge inn. Prøv igjen senere.');
      setLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 flex items-start justify-center p-4 pt-page-safe pb-24 overflow-y-auto auth-page-scroll"
      style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
    >
      <div ref={modalRef} className="w-full max-w-md sm:max-w-lg lg:max-w-xl xl:max-w-2xl mb-8 mt-10 sm:mt-12">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 lg:p-10 border-2 border-purple-200 relative">
          {onBack && (
            <button
              onClick={onBack}
              className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center rounded-full bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors shadow-sm active:scale-95"
              aria-label="Gå tilbake"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-3">
              <Sparkles className="w-12 h-12 text-purple-600" />
            </div>
            <h1 className="text-4xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-1 mb-2">Talentfunn</h1>
            <p className="text-gray-600">
              Vis hva du kan. Finn den som kan det.
            </p>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setIsSignUp(true)}
              className={`flex-1 py-3 px-4 rounded-xl transition-all ${
                isSignUp
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-purple-50 text-gray-600'
              }`}
            >
              Opprett profil
            </button>
            <button
              onClick={() => setIsSignUp(false)}
              className={`flex-1 py-3 px-4 rounded-xl transition-all ${
                !isSignUp
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-purple-50 text-gray-600'
              }`}
            >
              Logg inn
            </button>
          </div>

          <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
            {isSignUp && (
              <>
                <div>
                  <label htmlFor="username" className="block text-gray-700 mb-2">
                    Brukernavn
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    minLength={3}
                    maxLength={20}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={userType === 'talent' ? 'Mitt Brukernavn' : 'Min Bedrift AS'}
                  />
                  <p className="text-xs text-gray-500 mt-1">3-20 tegn, kun bokstaver, tall, mellomrom, bindestrek og understrek</p>
                  {checkingUsername && (
                    <p className="text-xs text-gray-500 mt-1">Sjekker tilgjengelighet...</p>
                  )}
                  {usernameAvailable === false && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      {usernameError || 'Brukernavn er allerede tatt'}
                    </p>
                  )}
                  {usernameAvailable === true && (
                    <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Brukernavn er tilgjengelig
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">
                    Velg rolle
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setUserType('talent')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        userType === 'talent'
                          ? 'bg-purple-50 border-purple-600 text-purple-700'
                          : 'bg-white border-purple-200 text-gray-600 hover:border-purple-400'
                      }`}
                    >
                      Privat
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserType('employer')}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        userType === 'employer'
                          ? 'bg-purple-50 border-purple-600 text-purple-700'
                          : 'bg-white border-purple-200 text-gray-600 hover:border-purple-400'
                      }`}
                    >
                      Bedrift
                    </button>
                  </div>
                </div>
              </>
            )}

            {isSignUp && userType === 'employer' && (
              <>
                <div>
                  <label htmlFor="organizationNumber" className="block text-gray-700 mb-2">
                    Organisasjonsnummer
                  </label>
                  <input
                    id="organizationNumber"
                    type="text"
                    value={organizationNumber}
                    onChange={(e) => setOrganizationNumber(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="123456789"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">
                    Din rolle i bedriften
                  </label>
                  <select
                    value={organizationRole}
                    onChange={(e) => setOrganizationRole(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Velg din rolle...</option>
                    <option value="Daglig leder">Daglig leder</option>
                    <option value="Styreleder">Styreleder</option>
                    <option value="Styremedlem">Styremedlem</option>
                    <option value="HR-ansvarlig">HR-ansvarlig</option>
                    <option value="Rekrutteringsansvarlig">Rekrutteringsansvarlig</option>
                    <option value="Avdelingsleder">Avdelingsleder</option>
                    <option value="Annet">Annet</option>
                  </select>
                </div>

                {organizationRole === 'Annet' && (
                  <div>
                    <label htmlFor="organizationRoleOther" className="block text-gray-700 mb-2">
                      Beskriv din rolle
                    </label>
                    <input
                      id="organizationRoleOther"
                      type="text"
                      value={organizationRoleOther}
                      onChange={(e) => setOrganizationRoleOther(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="F.eks. Eier, Partner, osv."
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="fullName" className="block text-gray-700 mb-2">
                    Fullt juridisk navn
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ola Nordmann"
                  />
                  <p className="text-xs text-gray-500 mt-1">Kreves for validering</p>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-sm">
                  <p className="text-blue-900 font-medium mb-1 flex items-center gap-2">
                    <Info className="w-4 h-4 flex-shrink-0" aria-hidden /> Verifisering påkrevd
                  </p>
                  <p className="text-blue-800">
                    Organisasjonsnummeret valideres mot Brønnøysundregisteret. Din konto må godkjennes av Talentfunn før du kan ta i bruk alle funksjoner.
                  </p>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="block text-gray-700 mb-2">
                E-post
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="din@epost.no"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-gray-700 mb-2">
                Passord
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete={isSignUp ? "new-password" : "current-password"}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
              />
              {isSignUp && (
                <div className="text-xs text-gray-500 mt-2 space-y-1 bg-purple-50 p-3 rounded-lg border border-purple-100">
                  <p className="font-semibold text-gray-700 mb-1">Passordkrav:</p>
                  <p className={`flex items-center gap-1 ${password.length >= 8 ? 'text-green-600' : ''}`}>
                    <Check className="w-3 h-3" />
                    Minst 8 tegn
                  </p>
                  <p className={`flex items-center gap-1 ${/[A-Z]/.test(password) ? 'text-green-600' : ''}`}>
                    <Check className="w-3 h-3" />
                    Minst én stor bokstav (A-Z)
                  </p>
                  <p className={`flex items-center gap-1 ${/[a-z]/.test(password) ? 'text-green-600' : ''}`}>
                    <Check className="w-3 h-3" />
                    Minst én liten bokstav (a-z)
                  </p>
                  <p className={`flex items-center gap-1 ${/[0-9]/.test(password) ? 'text-green-600' : ''}`}>
                    <Check className="w-3 h-3" />
                    Minst ett tall (0-9)
                  </p>
                  <p className={`flex items-center gap-1 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-600' : ''}`}>
                    <Check className="w-3 h-3" />
                    Minst ett spesialtegn (!@#$%^&* osv.)
                  </p>
                </div>
              )}
            </div>

            {isSignUp && showTermsLink && (
              <>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1"
                    id="accept-terms"
                  />
                  <label htmlFor="accept-terms" className="text-sm text-gray-500">
                    Jeg har lest og aksepterer{' '}
                    <button
                      type="button"
                      onClick={() => onTermsClick?.()}
                      className="text-purple-600 hover:underline focus:outline-none focus:ring-1 focus:ring-purple-300 rounded px-1"
                    >
                      vilkårene
                    </button>
                    , og bekrefter at jeg er <strong className="text-gray-700">15 år eller eldre</strong> og har samtykke fra foresatte hvis jeg er under 18 år
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mt-1"
                    id="accept-privacy"
                  />
                  <label htmlFor="accept-privacy" className="text-sm text-gray-500">
                    Jeg har lest og aksepterer{' '}
                    <button
                      type="button"
                      onClick={() => {
                        window.location.hash = 'privacy';
                      }}
                      className="text-purple-600 hover:underline focus:outline-none focus:ring-1 focus:ring-purple-300 rounded px-1"
                    >
                      personvernerklæringen
                    </button>
                    , og samtykker til behandling av mine personopplysninger som beskrevet
                  </label>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (isSignUp && (!acceptedTerms || !acceptedPrivacy))}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader size="md" />}
              {loading ? (isSignUp ? 'Oppretter profil...' : 'Logger inn...') : isSignUp ? 'Opprett profil' : 'Logg inn'}
            </button>

            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (onTermsClick) onTermsClick();
                  else window.location.hash = 'terms';
                }}
                className="hover:text-purple-600 underline transition-colors"
              >
                Vilkår
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.hash = 'privacy';
                }}
                className="hover:text-purple-600 underline transition-colors"
              >
                Personvern
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onFaqClick) onFaqClick();
                  else window.location.hash = 'faq';
                }}
                className="hover:text-purple-600 underline transition-colors"
              >
                FAQ
              </button>
            </div>

            {onForgotPassword && (
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-purple-600 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-300 rounded px-2 py-1 transition-all"
                >
                  Glemt passord?
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showEmailVerificationModal && (
        <EmailVerificationModal
          email={pendingVerificationEmail}
          userType={pendingVerificationUserType}
          onClose={() => setShowEmailVerificationModal(false)}
          onRetryLogin={handleRetryLogin}
          isAfterSignup={isAfterSignup} // Pass the flag to the modal
          loginError={error} // Pass login error to modal
          isLoggingIn={loading} // Pass loading state to modal
        />
      )}

    </div>
  );
}
